import * as Sentry from '@sentry/react';
import type { IOktaClaims } from '@sureapp/platform-sdk';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import startsWith from 'lodash/startsWith';
import moment from 'moment';
import qs from 'qs';

type TIdentifyArgs = (string | Object)[];

let cachedMergeProps = {};

const constructUser = (
  oktaClaims: Partial<IOktaClaims> = {},
  sureUser = {}
) => {
  // Sanitize oktaClaims
  const filteredOktaProps = {
    ...omit(oktaClaims, [
      `amr`,
      `aud`,
      `exp`,
      `iat`,
      `idp`,
      `iss`,
      `jti`,
      `nonce`,
      `sub`,
      `ver`,
      `StreetAddress`,
      `City`,
      `State`,
      `ZipCode`,
      `zoneinfo`,
      `given_name`,
      `family_name`,
      `preferred_username`,
    ]),
    // Normalize OKTA name and address props
    address: {
      line1: oktaClaims.StreetAddress,
      city: oktaClaims.City,
      region: {
        code: oktaClaims.State,
      },
      postal: oktaClaims.ZipCode,
    },
    first_name: oktaClaims.given_name,
    last_name: oktaClaims.family_name,
  };

  // Sanitize Sure user
  const filteredSureUser = omit(sureUser, [
    `id`,
    `client.id`,
    `client.person.id`,
    `client.person.dob`,
    `client.person.address.id`,
  ]);
  // Set Sure IDs and restrict DOB to year
  const baseObj = isEmpty(filteredSureUser)
    ? {}
    : {
        birth_year: moment(
          get(filteredSureUser, 'client.person.dob'),
          'YYYY-MM-DD'
        ).format('YYYY'),
        sure_user_id: get(filteredSureUser, 'id'),
        sure_client_id: get(filteredSureUser, 'client.id'),
      };
  const mergedUser = merge(
    baseObj,
    filteredOktaProps,
    get(filteredSureUser, 'client.person')
  );
  return mergedUser;
};

// Parse arguments for identify function
function parseArgs(args: TIdentifyArgs): { userId?: string; traits?: Object } {
  return {
    userId: args.filter((el) => typeof el === 'string')?.[0] as string,
    traits: args.filter((el) => typeof el === 'object' && el !== null)?.[0],
  };
}

const analytics = {
  /**
   * Initialize the analytics tool.
   *
   * @param {String} segmentWriteKey The Segment.io API write key.
   * @param {Object} [mergeProps] Any properties to merge with every analytics request.
   */
  load(segmentWriteKey: string, mergeProps = {}) {
    if (window.analytics && window.analytics.load) {
      cachedMergeProps = mergeProps;
      window.analytics.load(segmentWriteKey);
    }
  },

  page(pageName?: string, properties = {}) {
    if (window.analytics) {
      const mergedProps = merge({}, cachedMergeProps, properties);
      window.analytics.page(pageName, mergedProps);
    }
  },

  track(event: string, properties = {}) {
    if (window.analytics) {
      const mergedProps = merge({}, cachedMergeProps, properties);
      window.analytics.track(event, mergedProps);
    }
  },

  identify(...args: TIdentifyArgs) {
    const { userId, traits } = parseArgs(args);

    return new Promise((resolve) => {
      window.analytics?.identify(
        ...[
          userId,
          { ...(traits || {}), partner: process.env.REACT_APP_TENANT },
          resolve,
        ].filter(Boolean)
      );
    });
  },

  identifyUser(oktaClaims: Partial<IOktaClaims> = {}, sureUser = {}) {
    if (window.analytics) {
      const queryStr = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      });
      const utmParams = pickBy(queryStr, (_, key) => {
        return startsWith(key.toLowerCase(), 'utm');
      });

      const user = constructUser(oktaClaims, sureUser);

      const traits = {
        ...user,
        ...utmParams,
      };
      window.analytics.identify(oktaClaims.sub, {
        ...traits,
        partner: process.env.REACT_APP_TENANT,
      });

      Sentry.setUser(traits);
    }
  },

  alias(userId: string) {
    if (window.analytics) {
      window.analytics.alias(userId);
    }
  },

  clearMergeProps() {
    cachedMergeProps = {};
  },
};

export default analytics;
